import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, LockKeyhole, RotateCcw, Send } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { settingsService } from "@/services/settings.service";
import type { PlatformSetting } from "@/types";
import toast from "react-hot-toast";

const valueTypes: PlatformSetting["valueType"][] = [
  "STRING",
  "NUMBER",
  "BOOLEAN",
  "JSON",
  "URL",
  "SECRET_REFERENCE",
];

const initialDraft = {
  key: "",
  value: "",
  description: "",
  valueType: "STRING" as PlatformSetting["valueType"],
  scope: "PLATFORM",
  environment: "ALL",
  changeNote: "",
};

export default function SystemSettingsPage() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(initialDraft);
  const [scope, setScope] = useState("ALL");
  const query = useQuery({
    queryKey: ["admin-settings"],
    queryFn: settingsService.list,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
  const create = useMutation({
    mutationFn: settingsService.create,
    onSuccess: () => {
      setDraft(initialDraft);
      void refresh();
      toast.success("Typed setting created and published");
    },
    onError: () => toast.error("Could not create setting"),
  });
  const stage = useMutation({
    mutationFn: ({ key, value, description, changeNote }: { key: string; value: string; description?: string; changeNote: string }) =>
      settingsService.update(key, { value, description, changeNote }),
    onSuccess: () => {
      void refresh();
      toast.success("Draft version staged");
    },
  });
  const publish = useMutation({
    mutationFn: ({ key, version, reason }: { key: string; version: number; reason: string }) =>
      settingsService.publish(key, version, reason),
    onSuccess: () => {
      void refresh();
      toast.success("Setting version published");
    },
  });
  const rollback = useMutation({
    mutationFn: ({ key, version, reason }: { key: string; version: number; reason: string }) =>
      settingsService.rollback(key, version, reason),
    onSuccess: () => {
      void refresh();
      toast.success("Rollback published as a new version");
    },
  });

  const settings = useMemo(
    () => (query.data ?? []).filter((setting) => scope === "ALL" || setting.scope === scope),
    [query.data, scope],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Typed platform settings"
        subtitle="Validate, stage, publish and roll back configuration without exposing secrets."
      />

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-bold text-slate-900">Create setting</h3>
            <p className="text-xs text-slate-500">The initial version is published; later edits require a second administrator.</p>
          </div>
          <LockKeyhole className="h-5 w-5 text-slate-400" />
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Key" value={draft.key} onChange={(value) => setDraft({ ...draft, key: value.toUpperCase() })} />
          <Select label="Type" value={draft.valueType} options={valueTypes} onChange={(value) => setDraft({ ...draft, valueType: value as PlatformSetting["valueType"] })} />
          <Select label="Scope" value={draft.scope} options={["PLATFORM", "TENANT_DEFAULT", "BILLING", "SECURITY", "OPERATIONS"]} onChange={(value) => setDraft({ ...draft, scope: value })} />
          <Select label="Environment" value={draft.environment} options={["ALL", "DEVELOPMENT", "STAGING", "PRODUCTION"]} onChange={(value) => setDraft({ ...draft, environment: value })} />
          <Field label={draft.valueType === "SECRET_REFERENCE" ? "Secret reference" : "Value"} value={draft.value} onChange={(value) => setDraft({ ...draft, value })} />
          <Field label="Description" value={draft.description} onChange={(value) => setDraft({ ...draft, description: value })} />
          <div className="md:col-span-2">
            <Field label="Change note" value={draft.changeNote} onChange={(value) => setDraft({ ...draft, changeNote: value })} />
          </div>
          <button
            type="button"
            disabled={create.isPending || !draft.key || !draft.value || draft.changeNote.length < 8}
            onClick={() =>
              create.mutate({
                ...draft,
                description: draft.description || undefined,
                isSecret: draft.valueType === "SECRET_REFERENCE",
              })
            }
            className="mt-5 h-10 rounded-xl bg-slate-900 px-4 text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
          >
            Create & publish
          </button>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Configuration registry</h3>
          <select value={scope} onChange={(event) => setScope(event.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
            {["ALL", "PLATFORM", "TENANT_DEFAULT", "BILLING", "SECURITY", "OPERATIONS"].map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
        {query.isLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500">Loading settings…</div>
        ) : query.isError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">Settings could not be loaded.</div>
        ) : settings.length ? (
          settings.map((setting) => (
            <SettingCard
              key={setting.id}
              setting={setting}
              busy={stage.isPending || publish.isPending || rollback.isPending}
              onStage={(value, description, changeNote) => stage.mutate({ key: setting.key, value, description, changeNote })}
              onPublish={(version) => {
                const reason = window.prompt("Why should this setting version be published?");
                if (reason && reason.trim().length >= 8) publish.mutate({ key: setting.key, version, reason: reason.trim() });
              }}
              onRollback={(version) => {
                const reason = window.prompt(`Why roll back ${setting.key} to v${version}?`);
                if (reason && reason.trim().length >= 8) rollback.mutate({ key: setting.key, version, reason: reason.trim() });
              }}
            />
          ))
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500">No settings match this scope.</div>
        )}
      </section>
    </div>
  );
}

function SettingCard({
  setting,
  busy,
  onStage,
  onPublish,
  onRollback,
}: {
  setting: PlatformSetting;
  busy: boolean;
  onStage: (value: string, description: string, changeNote: string) => void;
  onPublish: (version: number) => void;
  onRollback: (version: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [value, setValue] = useState(setting.isSecret ? "" : setting.value);
  const [description, setDescription] = useState(setting.description ?? "");
  const [changeNote, setChangeNote] = useState("");
  const versions = setting.versions ?? [];
  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <button type="button" onClick={() => setExpanded(!expanded)} className="flex w-full items-center justify-between gap-4 p-5 text-left">
        <div className="flex min-w-0 items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-bold text-slate-900">{setting.key}</span>
              <Badge>{setting.valueType}</Badge><Badge>{setting.scope}</Badge><Badge>{setting.environment}</Badge>
              {setting.isSecret && <Badge>SECRET</Badge>}
            </div>
            <p className="mt-1 truncate text-xs text-slate-500">{setting.description || "No description"}</p>
          </div>
        </div>
        <div className="text-right"><div className="text-xs font-bold text-slate-700">v{setting.activeVersion}</div><div className="text-[10px] uppercase text-emerald-600">Published</div></div>
      </button>
      {expanded && (
        <div className="grid gap-6 border-t border-slate-100 p-5 lg:grid-cols-2">
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Stage new version</h4>
            <Field label={setting.isSecret ? "New secret reference" : "Value"} value={value} onChange={setValue} />
            <Field label="Description" value={description} onChange={setDescription} />
            <Field label="Change note" value={changeNote} onChange={setChangeNote} />
            <button type="button" disabled={busy || !value || changeNote.length < 8} onClick={() => onStage(value, description, changeNote)} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"><Send className="h-3.5 w-3.5" /> Stage draft</button>
          </div>
          <div>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Version history</h4>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {versions.map((version) => (
                <div key={version.id} className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
                  <div><span className="text-sm font-bold">v{version.version}</span><span className={`ml-2 text-[10px] font-bold uppercase ${version.status === "DRAFT" ? "text-amber-600" : "text-slate-400"}`}>{version.status}</span><p className="mt-1 text-xs text-slate-500">{version.changeNote}</p></div>
                  <div className="flex gap-2">
                    {version.status === "DRAFT" && <button disabled={busy} onClick={() => onPublish(version.version)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-[10px] font-bold uppercase text-white">Publish</button>}
                    {version.status !== "DRAFT" && version.version !== setting.activeVersion && <button disabled={busy} onClick={() => onRollback(version.version)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-slate-600"><RotateCcw className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="block"><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span><input value={value} onChange={(event) => onChange(event.target.value)} className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" /></label>;
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: readonly string[]; onChange: (value: string) => void }) {
  return <label className="block"><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span><select value={value} onChange={(event) => onChange(event.target.value)} className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm">{options.map((option) => <option key={option}>{option}</option>)}</select></label>;
}

function Badge({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase text-slate-500">{children}</span>;
}
